import React, { useEffect, useState, useCallback } from 'react';
import { projectApi } from '../api/client';
import { ProjectRead, LayoutRequest, LayoutResponse } from '../types';

interface Props {
  refreshKey: number;
  onLoad: (req: LayoutRequest, res: LayoutResponse, name: string) => void;
}

export function ProjectsSidebar({ refreshKey, onLoad }: Props) {
  const [projects, setProjects] = useState<ProjectRead[]>([]);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await projectApi.list();
      setProjects(list);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleLoad = async (id: string) => {
    try {
      const detail = await projectApi.get(id);
      onLoad(detail.request, detail.response, detail.name);
      setOpen(false);
    } catch {}
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeleting(id);
    try {
      await projectApi.delete(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
    } catch {}
    finally { setDeleting(null); }
  };

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
    });

  return (
    <>
      {/* Floating button */}
      <button style={fabStyle} onClick={() => setOpen(!open)} title="Saved projects">
        📁
        {projects.length > 0 && (
          <span style={badgeStyle}>{projects.length}</span>
        )}
      </button>

      {/* Drawer */}
      {open && (
        <div style={drawerStyle}>
          <div style={drawerHeader}>
            <span>Saved Projects</span>
            <button style={closeBtn} onClick={() => setOpen(false)}>✕</button>
          </div>

          {loading ? (
            <div style={emptyStyle}>Loading…</div>
          ) : projects.length === 0 ? (
            <div style={emptyStyle}>No saved projects yet.<br />
              Generate a layout and click "Save as project".</div>
          ) : (
            <div style={listStyle}>
              {projects.map((p) => (
                <div key={p.id} style={itemStyle} onClick={() => handleLoad(p.id)}>
                  <div style={itemName}>{p.name}</div>
                  <div style={itemMeta}>
                    {p.total_dc_mwp != null && (
                      <span style={chip}>{p.total_dc_mwp.toFixed(1)} MWp</span>
                    )}
                    {p.site_area_ha != null && (
                      <span style={chip}>{p.site_area_ha.toFixed(0)} ha</span>
                    )}
                    <span style={dateChip}>{fmt(p.created_at)}</span>
                  </div>
                  <button
                    style={deleteBtn}
                    onClick={(e) => handleDelete(e, p.id)}
                    disabled={deleting === p.id}
                    title="Delete project"
                  >
                    {deleting === p.id ? '…' : '🗑'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

const C = { navy: '#1F3864', blue: '#2E5FA3', border: '#e0e5ee' };

const fabStyle: React.CSSProperties = {
  position: 'absolute', bottom: 90, right: 16, zIndex: 10,
  width: 48, height: 48, borderRadius: '50%',
  background: C.navy, color: '#fff', border: 'none',
  fontSize: 20, cursor: 'pointer',
  boxShadow: '0 3px 12px rgba(0,0,0,0.3)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const badgeStyle: React.CSSProperties = {
  position: 'absolute', top: -4, right: -4,
  background: '#C55A11', color: '#fff',
  borderRadius: '50%', width: 18, height: 18,
  fontSize: 10, fontWeight: 700,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const drawerStyle: React.CSSProperties = {
  position: 'absolute', bottom: 150, right: 16, zIndex: 20,
  width: 320, maxHeight: 420, background: '#fff', borderRadius: 10,
  boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
  fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
};
const drawerHeader: React.CSSProperties = {
  padding: '12px 16px', background: C.navy, color: '#fff',
  fontWeight: 700, fontSize: 14, display: 'flex',
  justifyContent: 'space-between', alignItems: 'center',
};
const closeBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: '#fff',
  fontSize: 16, cursor: 'pointer', padding: '0 4px',
};
const listStyle: React.CSSProperties = { overflowY: 'auto', flex: 1 };
const emptyStyle: React.CSSProperties = {
  padding: '20px 16px', color: '#999', fontSize: 13, textAlign: 'center', lineHeight: 1.6,
};
const itemStyle: React.CSSProperties = {
  padding: '10px 16px', borderBottom: `1px solid ${C.border}`,
  cursor: 'pointer', position: 'relative',
  transition: 'background 0.1s',
};
const itemName: React.CSSProperties = {
  fontWeight: 600, fontSize: 13, color: '#1F3864', marginBottom: 5,
  paddingRight: 28,
};
const itemMeta: React.CSSProperties = { display: 'flex', gap: 5, flexWrap: 'wrap' as const };
const chip: React.CSSProperties = {
  background: '#d6e4f7', color: '#2E5FA3', borderRadius: 10,
  padding: '2px 7px', fontSize: 11, fontWeight: 600,
};
const dateChip: React.CSSProperties = {
  background: '#f0f0f0', color: '#888', borderRadius: 10,
  padding: '2px 7px', fontSize: 11,
};
const deleteBtn: React.CSSProperties = {
  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
  background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 4,
};
